import React from "react";
import Gist from "../../../components/gist/Gist";
import styles from "../SubjectSinglePage.module.css";
import { CONTEXT_REPLY_BATCH_SIZE } from "../model/constants";
import type { GistItem } from "../model/types";
import {
  buildLegacySingleGistWorkspace,
  countGistMetrics,
  getAncestorGists,
  getReplyGists,
  sortContextGists,
} from "../model/subjectSingleModel";

type AdvancedSequenceItem = {
  gist: GistItem;
  role: "ancestor" | "current" | "reply";
  label: string;
};

type AdvancedModeProps = {
  gist: GistItem;
  gists: GistItem[];
  visibleReplyGistCount: number;
  annotationIntentGistId: number | null;
  onOpenGistReader: (gist: GistItem, mode: "advanced") => void;
  onDismissAnnotationHint: () => void;
  onLoadMoreReplies: () => void;
  onAfterDelete: () => Promise<void>;
};

export const AdvancedMode: React.FC<AdvancedModeProps> = ({
  gist,
  gists,
  visibleReplyGistCount,
  annotationIntentGistId,
  onOpenGistReader,
  onDismissAnnotationHint,
  onLoadMoreReplies,
  onAfterDelete,
}) => {
  const ancestors = getAncestorGists(gist, gists).slice(-2);
  const replyGists = sortContextGists(getReplyGists(gist, gists));
  const visibleReplies = replyGists.slice(0, visibleReplyGistCount);
  const hiddenReplyCount = Math.max(0, replyGists.length - visibleReplies.length);
  const advancedSequence: AdvancedSequenceItem[] = [
    ...ancestors.map((ancestor, index) => ({
      gist: ancestor,
      role: "ancestor" as const,
      label: index === ancestors.length - 1 ? "Direct parent gist" : "Older parent gist",
    })),
    { gist, role: "current" as const, label: "Current gist" },
    ...visibleReplies.map((reply, index) => ({
      gist: reply,
      role: "reply" as const,
      label: `Reply gist ${index + 1}`,
    })),
  ];

  const renderAdvancedLegacySection = (item: AdvancedSequenceItem) => {
    const metrics = countGistMetrics(item.gist);
    const replies = getReplyGists(item.gist, gists).length;
    const isCurrent = item.role === "current";
    const showAnnotationHint =
      isCurrent && annotationIntentGistId === item.gist.id;
    const legacyWorkspaceGists = buildLegacySingleGistWorkspace(item.gist);

    let eyebrowText = "Gist workspace";
    if (item.role === "ancestor") eyebrowText = "Parent gist workspace";
    else if (item.role === "current") eyebrowText = "Current gist workspace";
    else if (item.role === "reply") eyebrowText = "Reply gist workspace";

    return (
      <section
        key={`advanced-${item.role}-${item.gist.id}`}
        className={`${styles.advancedLegacySequenceItem} ${
          isCurrent ? styles.advancedLegacySequenceItemCurrent : ""
        }`}
        aria-label={`${eyebrowText}: ${item.gist.title}`}
      >
        <div className={styles.advancedLegacySequenceHeader}>
          <div>
            <span className={styles.advancedEyebrow}>{eyebrowText}</span>
            <h3 className={styles.advancedTitle}>{item.gist.title}</h3>
            <p className={styles.advancedHelperText}>
              Full controls for versions, edits, comments, annotations, and lifecycle.
            </p>
          </div>
          <div className={styles.advancedLegacySequenceMeta}>
            <span>{metrics.commentCount} comments</span>
            <span>{replies} replies</span>
            {metrics.editCount > 0 && <span>{metrics.editCount} edits</span>}
            {!isCurrent && (
              <button
                type="button"
                onClick={() => onOpenGistReader(item.gist, "advanced")}
                aria-label={`Make ${item.gist.title} the current advanced gist`}
              >
                Make current
              </button>
            )}
          </div>
        </div>

        {showAnnotationHint && (
          <div
            className={styles.annotationIntentHint}
            role="status"
            aria-live="polite"
          >
            <div>
              <strong>Select text in this gist to create an anchored comment.</strong>
              <span>
                Highlight a sentence or paragraph, then use the composer that appears.
              </span>
            </div>
            <button
              type="button"
              onClick={onDismissAnnotationHint}
              aria-label="Dismiss annotation guidance"
            >
              ×
            </button>
          </div>
        )}

        <div className={styles.advancedLegacyReader}>
          {legacyWorkspaceGists.length ? (
            <Gist gists={legacyWorkspaceGists} onAfterDelete={onAfterDelete} />
          ) : (
            <div className={styles.emptyReaderPanel}>
              <h2>No advanced gist data available</h2>
              <p>
                This gist cannot be loaded into the advanced workspace yet.
              </p>
            </div>
          )}
        </div>
      </section>
    );
  };

  return (
    <section className={styles.advancedLegacyShell} aria-label="Advanced legacy gist workspace">
      <div className={styles.advancedLegacyIntro}>
        <div>
          <span className={styles.modeEyebrow}>Advanced workspace</span>
          <h2>Full controls for the same reading strip</h2>
          <p>
            Advanced now follows the same parent/current/reply gist sequence
            used by Read and Tools, but each visible gist is rendered through
            the legacy power-reader so version navigation, edit controls,
            lifecycle actions, moderation, comments, anchored annotations,
            and focus mode remain available.
          </p>
        </div>
        <div className={styles.advancedLegacyBadges} aria-label="Available advanced controls">
          <span>{ancestors.length} parents</span>
          <span>1 current</span>
          <span>{visibleReplies.length} replies</span>
          <span>Versions</span>
          <span>Edits</span>
          <span>Comments</span>
          <span>Lifecycle</span>
        </div>
      </div>

      <div className={styles.advancedLegacySequence}>
        {advancedSequence.length ? (
          advancedSequence.map(renderAdvancedLegacySection)
        ) : (
          <div className={styles.emptyReaderPanel}>
            <h2>No advanced gist data available</h2>
            <p>
              This subject does not have any gists that can be loaded into the
              advanced workspace yet.
            </p>
          </div>
        )}
      </div>

      {hiddenReplyCount > 0 && (
        <div className={styles.contextLoadMore}>
          <button type="button" onClick={onLoadMoreReplies}>
            Load next {Math.min(CONTEXT_REPLY_BATCH_SIZE, hiddenReplyCount)} advanced reply gists ↓
          </button>
          <span>{hiddenReplyCount} still hidden</span>
        </div>
      )}
    </section>
  );
};
